import React from 'react';
import { StepProps, FormErrorsStep, FreeZoneScheduleData, FormData, TaxablePersonDetails } from '../../types';
import { Checkbox } from '../ui/Checkbox';
import { RadioGroup } from '../ui/RadioGroup';
import { FreeZoneSchedule } from '../schedules/FreeZoneSchedule';
import { translations } from '../../i18n/translations';
import { getAccountingBasis } from '../../utils/calculations';

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <h3 className="text-md font-semibold text-[#1a2c46] mt-4 mb-2 border-b border-[#b98e4c] pb-1">{title}</h3>
);


export const Step2Elections: React.FC<StepProps> = ({ formData, onChange, errors, t }) => {
  const data = formData.elections;
  const taxablePersonData = formData.taxablePersonDetails;

  const getTurnover = (details: TaxablePersonDetails): string => {
    if (details.filingAs === 'natural_person') return details.naturalPersonSpecifics?.turnover || '0';
    if (details.filingAs === 'juridical_person' || details.filingAs === 'free_zone_person') return details.juridicalPersonSpecifics?.revenueForTaxPeriod || '0';
    if (details.filingAs === 'unincorporated_partnership') return details.unincorporatedPartnershipSpecifics?.revenueForTaxPeriod || '0';
    if (details.filingAs === 'tax_group_parent') return details.taxGroupSpecifics?.consolidatedRevenue || '0';
    return '0';
  };

  
  const getIsBankOrInsuranceProvider = (details: TaxablePersonDetails): 'yes' | 'no' | '' => {
    if (details.filingAs === 'juridical_person' || details.filingAs === 'free_zone_person') {
        return details.juridicalPersonSpecifics?.isBankOrInsuranceProvider || '';
    }
    if (details.filingAs === 'tax_group_parent') { // Check if any member is a bank/insurance for Tax Group
        // This logic might need refinement based on how Tax Group member types are stored.
        // Assuming taxGroupSpecifics might have member details or a flag.
        // For now, let's assume a direct flag if available, or default to 'no'.
        // return details.taxGroupSpecifics?.anyMemberIsBankOrInsurance || '';
        return ''; // Placeholder: Needs proper logic for tax group members
    }
    return '';
  }

  const getIsFzPerson = (details: TaxablePersonDetails): 'yes' | 'no' | '' => {
      if (details.filingAs === 'free_zone_person') return 'yes'; 
      if (details.filingAs === 'juridical_person') {
          return details.juridicalPersonSpecifics?.isFreeZonePerson || '';
      }
      return '';
  }
  
  const getIsMneMember = (details: TaxablePersonDetails): 'yes' | 'no' | '' => {
      if (details.filingAs === 'juridical_person' || details.filingAs === 'free_zone_person') return details.juridicalPersonSpecifics?.isMneGroupMember || '';
      if (details.filingAs === 'natural_person') return 'no'; 
      if (details.filingAs === 'unincorporated_partnership') return details.unincorporatedPartnershipSpecifics?.isMneGroupMember || '';
      if (details.filingAs === 'tax_group_parent') return details.taxGroupSpecifics?.isMneGroupMember || '';
      return '';
  }


  const getFzElectionForego0Rate = (details: TaxablePersonDetails): 'yes' | 'no' | '' => {
      if (details.filingAs === 'juridical_person' || details.filingAs === 'free_zone_person') {
          return details.juridicalPersonSpecifics?.electionToForego0RateFZ || '';
      }
      return '';
  }

  const getTaxResidencyStatus = (details: TaxablePersonDetails): 'resident' | 'non-resident' | '' => {
    if (details.filingAs === 'natural_person') {
        if(details.naturalPersonSpecifics?.taxResidencyStatusDTA === 'resident') return 'resident';
        if(details.naturalPersonSpecifics?.taxResidencyStatusDTA === 'non-resident') return 'non-resident';
        return details.naturalPersonSpecifics?.uaeResidencyStatus === 'uae_resident' ? 'resident' : 'non-resident';
    }
    if (details.filingAs === 'juridical_person' || details.filingAs === 'free_zone_person') {
        const jpSpecifics = details.juridicalPersonSpecifics;
        if (jpSpecifics?.isUaeOrFzIncorporated === 'yes') {
            return jpSpecifics?.isNonResidentInUaeUnderDTA === 'yes' ? 'non-resident' : 'resident';
        }
        if (jpSpecifics?.isForeignIncorporatedUaeControlled === 'yes') {
             return jpSpecifics?.isTaxResidentInForeignJurisdictionUnderDTA === 'yes' ? 'non-resident' : 'resident';
        }
        return 'non-resident'; 
    }
    return 'resident'; 
  }


  const revenue = parseFloat(getTurnover(taxablePersonData) || '0');
  const isEligibleForSBR = revenue <= 3000000 && 
                           getTaxResidencyStatus(taxablePersonData) === 'resident' && 
                           !(getIsFzPerson(taxablePersonData) === 'yes' && getFzElectionForego0Rate(taxablePersonData) === 'no') && 
                           getIsMneMember(taxablePersonData) !== 'yes';


  const showFreeZoneSchedule = getIsFzPerson(taxablePersonData) === 'yes' && getFzElectionForego0Rate(taxablePersonData) === 'no';

  const handleFzScheduleChange = (field: keyof typeof formData.freeZoneSchedule, value: any) => {
    onChange('freeZoneSchedule', field, value);
  };

  const showRealisationBankOption = data.realisationBasis && getIsBankOrInsuranceProvider(taxablePersonData) === 'yes';
  const isFirstTaxPeriod = true; // Placeholder - In a real app, this would be determined by TP registration date / tax period start.
  const historicalCostBasisUsed = true; // Placeholder - Determined from accounting standards chosen or specific declarations.
  const currentAccountingBasis = getAccountingBasis(taxablePersonData);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-[#1a2c46] mb-6">{t('step2_title')}</h2>

      <Checkbox
        label={t('realisation_basis_label')}
        id="realisationBasis"
        name="realisationBasis"
        checked={data.realisationBasis}
        onChange={(e) => onChange('elections', 'realisationBasis', e.target.checked)}
        error={errors.realisationBasis as string}
        disabled={currentAccountingBasis === 'cash' || !isFirstTaxPeriod}
        helpText={currentAccountingBasis === 'cash' ? t('realisation_basis_cash_disabled') : (isFirstTaxPeriod ? t('help_s2_realisation_basis') : t('election_first_period_only'))}
      />

      {showRealisationBankOption && (
        <RadioGroup
          label={t('realisation_basis_bank_option_label')}
          name="realisationBasisBankOption"
          value={data.realisationBasisBankOption || ''}
          onChange={(val) => onChange('elections', 'realisationBasisBankOption', val)}
          options={[
            { label: t('realisation_basis_bank_option_a'), value: 'A' },
            { label: t('realisation_basis_bank_option_b'), value: 'B' },
          ]}
          error={errors.realisationBasisBankOption as string}
          helpText={t('realisation_basis_bank_option_help') + " " + t('s5_1_3_ctgtxr1_ref')}
          t={t}
        />
      )}

      <SectionHeader title={t('transitional_rules_section_title')} />
      <Checkbox
        label={t('transitional_rules_immovable_property_label')}
        id="transitionalRulesImmovableProperty"
        checked={data.transitionalRulesImmovableProperty}
        onChange={e => onChange('elections', 'transitionalRulesImmovableProperty', e.target.checked)}
        error={errors.transitionalRulesImmovableProperty as string}
        disabled={!isFirstTaxPeriod || currentAccountingBasis === 'cash' || !historicalCostBasisUsed}
        helpText={(isFirstTaxPeriod && currentAccountingBasis !== 'cash' && historicalCostBasisUsed ? t('help_s2_transitional_rules') : t('transitional_rules_conditions_not_met')) + " " + t('s5_2_ctgtxr1_ref')}
      />
      <Checkbox
        label={t('transitional_rules_intangible_assets_label')}
        id="transitionalRulesIntangibleAssets"
        checked={data.transitionalRulesIntangibleAssets}
        onChange={e => onChange('elections', 'transitionalRulesIntangibleAssets', e.target.checked)}
        error={errors.transitionalRulesIntangibleAssets as string}
        disabled={!isFirstTaxPeriod || currentAccountingBasis === 'cash' || !historicalCostBasisUsed}
        helpText={(isFirstTaxPeriod && currentAccountingBasis !== 'cash' && historicalCostBasisUsed ? t('help_s2_transitional_rules') : t('transitional_rules_conditions_not_met')) + " " + t('s5_2_ctgtxr1_ref')}
      />
      <Checkbox
        label={t('transitional_rules_financial_assets_liabilities_label')}
        id="transitionalRulesFinancialAssetsLiabilities"
        checked={data.transitionalRulesFinancialAssetsLiabilities}
        onChange={e => onChange('elections', 'transitionalRulesFinancialAssetsLiabilities', e.target.checked)}
        error={errors.transitionalRulesFinancialAssetsLiabilities as string}
        disabled={!isFirstTaxPeriod || currentAccountingBasis === 'cash' || !historicalCostBasisUsed}
        helpText={(isFirstTaxPeriod && currentAccountingBasis !== 'cash' && historicalCostBasisUsed ? t('help_s2_transitional_rules') : t('transitional_rules_conditions_not_met')) + " " + t('s5_2_ctgtxr1_ref')}
      />


      {isEligibleForSBR ? (
        <Checkbox
          label={t('sbr_election_label')}
          id="smallBusinessRelief"
          name="smallBusinessRelief"
          checked={data.smallBusinessRelief}
          onChange={(e) => onChange('elections', 'smallBusinessRelief', e.target.checked)}
          error={errors.smallBusinessRelief as string}
          helpText={t('help_s2_sbr')}
        />
      ) : (
        <p className="p-3 bg-yellow-100 border border-yellow-300 text-yellow-700 rounded-md text-sm">
          {t('sbr_not_eligible_message', { revenue_limit: "3,000,000"})}
        </p>
      )}

      <SectionHeader title={t('group_and_restructuring_reliefs_title')} />
       <Checkbox
        label={t('transfers_within_qualifying_group_label')}
        id="transfersWithinQualifyingGroup"
        checked={data.transfersWithinQualifyingGroup}
        onChange={e => onChange('elections', 'transfersWithinQualifyingGroup', e.target.checked)}
        error={errors.transfersWithinQualifyingGroup as string}
        helpText={t('help_s2_qg_relief')}
      />
      {data.transfersWithinQualifyingGroup && (
        <RadioGroup
          label={t('election_qg_transfer_occurred_label')}
          name="hasTransfersWithinQGInPeriod"
          value={data.hasTransfersWithinQGInPeriod ? 'yes' : 'no'}
          onChange={val => onChange('elections', 'hasTransfersWithinQGInPeriod', val === 'yes')}
          options={[{label: t('yes'), value: 'yes'}, {label: t('no'), value: 'no'}]}
          t={t}
          helpText={t('election_qg_transfer_occurred_help')}
        />
      )}

      <Checkbox
        label={t('business_restructuring_relief_label')}
        id="businessRestructuringRelief"
        checked={data.businessRestructuringRelief}
        onChange={e => onChange('elections', 'businessRestructuringRelief', e.target.checked)}
        error={errors.businessRestructuringRelief as string}
        helpText={t('help_s2_brr_relief')}
      />
      {data.businessRestructuringRelief && (
         <RadioGroup
          label={t('election_brr_transfer_occurred_label')}
          name="hasBusinessRestructuringInPeriod"
          value={data.hasBusinessRestructuringInPeriod ? 'yes' : 'no'}
          onChange={val => onChange('elections', 'hasBusinessRestructuringInPeriod', val === 'yes')}
          options={[{label: t('yes'), value: 'yes'}, {label: t('no'), value: 'no'}]}
          t={t}
          helpText={t('election_brr_transfer_occurred_help')}
        />
      )}

      <SectionHeader title={t('foreign_operations_title')} />
      <RadioGroup
        label={t('election_has_foreign_pes_label')}
        name="hasForeignPEs"
        value={data.hasForeignPEs ? 'yes' : 'no'}
        onChange={val => onChange('elections', 'hasForeignPEs', val === 'yes')}
        options={[{label: t('yes'), value: 'yes'}, {label: t('no'), value: 'no'}]}
        error={errors.hasForeignPEs as string}
        t={t}
        helpText={t('s5_6_ctgtxr1_ref')}
      />
      {data.hasForeignPEs && (
        <Checkbox
          label={t('fpe_exemption_label')}
          id="foreignPermanentEstablishmentExemption"
          name="foreignPermanentEstablishmentExemption"
          checked={data.foreignPermanentEstablishmentExemption}
          onChange={(e) => onChange('elections', 'foreignPermanentEstablishmentExemption', e.target.checked)}
          error={errors.foreignPermanentEstablishmentExemption as string}
          helpText={t('help_s2_fpe_exemption')}
        />
      )}


      {showFreeZoneSchedule && (
        <div className="mt-6 p-4 border-2 border-[#b98e4c] rounded-lg bg-yellow-50 shadow">
           <p className="text-sm text-[#1a2c46] mb-3">{t('fz_schedule_prompt')}</p>
          <FreeZoneSchedule
            scheduleData={formData.freeZoneSchedule}
            onChange={handleFzScheduleChange as any} 
            errors={errors.freeZoneSchedule as FormErrorsStep || {}}
            t={t}
            fullFormData={formData}
          />
        </div>
      )}
      <p className="mt-4 text-sm text-gray-600">{t('step2_note')}</p>
    </div>
  );
};